import React from 'react';
import { Card, Col, Row, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { aiGovernanceService } from '@/services/aiGovernance.service';
import {
  HeroKpiCard,
  OiEmptyState,
  OiPageFrame,
  PrimaryKpiCard,
} from '@/components/owner-intelligence/OwnerIntelligenceComponents';

const AiGovernancePage: React.FC = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['ai-governance-dashboard'],
    queryFn: () => aiGovernanceService.getDashboard(),
    staleTime: 60_000,
  });
  const analyzersQ = useQuery({
    queryKey: ['ai-governance-analyzers'],
    queryFn: () => aiGovernanceService.getAnalyzers(),
    staleTime: 60_000,
  });
  const driftQ = useQuery({
    queryKey: ['ai-governance-drift'],
    queryFn: () => aiGovernanceService.getDrift(20),
    staleTime: 60_000,
  });
  const benchQ = useQuery({
    queryKey: ['ai-governance-benchmarks'],
    queryFn: () => aiGovernanceService.getBenchmarks(),
    staleTime: 60_000,
  });

  return (
    <OiPageFrame
      subtitle="Prediction accuracy, trust, analyzer health, and drift — AI accountability."
      loading={isLoading && !data}
      error={Boolean(error)}
      errorMessage="AI governance unavailable"
    >
      {!data ? (
        <OiEmptyState title="No governance data" body="Generate approval briefs to populate predictions and trust." />
      ) : (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} md={8}>
              <HeroKpiCard
                label="Prediction accuracy (90d)"
                value={data.accuracy_pct != null ? `${data.accuracy_pct}%` : '—'}
                score={data.accuracy_pct ?? undefined}
              />
            </Col>
            <Col xs={12} md={4}>
              <PrimaryKpiCard label="Avg trust" value={data.avg_trust ?? '—'} />
            </Col>
            <Col xs={12} md={4}>
              <PrimaryKpiCard label="Predictions" value={data.predictions_90d} />
            </Col>
            <Col xs={12} md={4}>
              <PrimaryKpiCard label="Feedback" value={data.feedback.total} />
            </Col>
            <Col xs={12} md={4}>
              <PrimaryKpiCard label="Drift events" value={data.drift_events_90d} />
            </Col>
          </Row>

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={8} md={4}>
              <PrimaryKpiCard label="Trust Very High" value={data.trust_distribution.very_high} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <PrimaryKpiCard label="Trust High" value={data.trust_distribution.high} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <PrimaryKpiCard label="Trust Moderate" value={data.trust_distribution.moderate} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <PrimaryKpiCard label="Trust Limited" value={data.trust_distribution.limited} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <PrimaryKpiCard label="Trust Low" value={data.trust_distribution.low} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <PrimaryKpiCard
                label="Helpful / Not"
                value={`${data.feedback.helpful} / ${data.feedback.not_helpful}`}
              />
            </Col>
          </Row>

          <Card title="Benchmarks (accuracy windows)" bordered={false} style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col xs={12}>
                <PrimaryKpiCard
                  label="Current 90d"
                  value={
                    benchQ.data?.current_90d.accuracy_pct != null
                      ? `${benchQ.data.current_90d.accuracy_pct}%`
                      : '—'
                  }
                />
              </Col>
              <Col xs={12}>
                <PrimaryKpiCard
                  label="Prior 90d"
                  value={
                    benchQ.data?.prior_90d.accuracy_pct != null
                      ? `${benchQ.data.prior_90d.accuracy_pct}%`
                      : '—'
                  }
                />
              </Col>
            </Row>
          </Card>

          <Card title="Analyzer health" bordered={false} style={{ marginBottom: 16 }}>
            <Table
              size="small"
              rowKey="analyzer_key"
              pagination={false}
              loading={analyzersQ.isLoading}
              dataSource={analyzersQ.data ?? []}
              columns={[
                { title: 'Analyzer', dataIndex: 'analyzer_key' },
                { title: 'Version', dataIndex: 'version' },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  render: (v: string) => <Tag>{v}</Tag>,
                },
                { title: 'Predictions (90d)', dataIndex: 'prediction_count_90d' },
                { title: 'Avg confidence', dataIndex: 'avg_confidence' },
                { title: 'Avg trust', dataIndex: 'avg_trust' },
              ]}
            />
          </Card>

          <Card title="Recent drift alerts" bordered={false}>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              loading={driftQ.isLoading}
              dataSource={(driftQ.data as Array<Record<string, unknown>>) ?? []}
              locale={{ emptyText: 'No drift events' }}
              columns={[
                { title: 'Metric', dataIndex: 'metric_key' },
                { title: 'Window', dataIndex: 'window_label' },
                { title: 'Prior %', dataIndex: 'prior_pct' },
                { title: 'Current %', dataIndex: 'current_pct' },
                {
                  title: 'Severity',
                  dataIndex: 'severity',
                  render: (v: string) => <Tag color={v === 'critical' ? 'red' : 'orange'}>{v}</Tag>,
                },
                { title: 'When', dataIndex: 'created_at' },
              ]}
            />
          </Card>
        </>
      )}
    </OiPageFrame>
  );
};

export default AiGovernancePage;
